<template>
	<div>
		<policy-submenu />
		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline">
					{{ $t("claims") }}
				</template>
				<template slot="actions">
					<j-button
						v-can="'writeClaims'"
						:to="{ name: 'ClaimCreate', query: { policyId: this.id } }"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("create_claim") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedList"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template>
								<!-- Actions -->
								<template v-if="column.field === 'actions'"> </template>

								<template v-else-if="column.field === 'issuerNumber'">
									<router-link
										:to="{
											name: `ClaimDetail`,
											params: { id: item.id }
										}"
										>{{
											item.issuerNumber ? item.issuerNumber : "---"
										}}</router-link
									>
								</template>

								<!-- Name -->
								<template v-else-if="column.field === 'name'">
									<router-link v-if="item.claimant" :to="item.claimant.to">{{
										item.claimant.name
									}}</router-link>
								</template>

								<!-- Policy -->
								<template v-else-if="column.field === 'policy'">
									<router-link v-if="item.policy" :to="item.policy.to">{{
										item.policy.issuerNumber
									}}</router-link>
								</template>

								<!-- Status -->
								<template v-else-if="column.field === 'status'">
									<j-tag
										:variant="
											(status =>
												({
													PROCESSING: 'warning',
													APPROVED: 'success',
													REJECTED: 'error'
												}[status]))(item.status)
										"
										:label="item.status"
									/>
								</template>

								<!-- Others -->
								<template v-else>
									{{ item[column.field] }}
								</template>
							</template>
						</td>
					</template>
					<template slot="empty">
						{{ $t("errors.no_data") }}
					</template>
				</j-data-table>
			</j-card>
		</core-layout-content>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JTag from "@/components/JTag"
import JDataTable from "@/components/JDataTable/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import PolicySubmenu from "../components/PolicySubmenu"
import dayjs from "dayjs"
import api from "../api/"
import crmConfig from "@/config/"
import get from "lodash.get"
import debounce from "lodash.debounce"

const getClaimantFromItem = item =>
	item?.claimant
		? {
				id: item.claimant.id,
				name: item.claimant.name,
				to: {
					name: `ClientDetail`,
					params: {
						clientType: "individual",
						id: item.claimant.id
					}
				}
		  }
		: null

const getPolicyFromItem = item =>
	item.policy?.id
		? {
				issuerNumber: item.policy.issuerNumber
					? `#${item.policy.issuerNumber}`
					: null,
				to: {
					name: `PolicyDetail`,
					params: {
						id: item.policy.id
					}
				}
		  }
		: null

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JBaseInputSearch,
		JPagination,
		JTag,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		PolicySubmenu
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Header of table
			header: [
				{
					field: "issuerNumber",
					text: this.$t("claim_number")
				},
				{
					field: "name",
					text: this.$t("client")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("status"),
					field: "status"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			]
		}
	},

	props: {
		// From router
		id: String
	},

	computed: {
		computedList() {
			return this.listing.list.map(item => ({
				...item,
				claimant: getClaimantFromItem(item),
				policy: getPolicyFromItem(item),
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		}
	},

	methods: {
		async readAll() {
			// Api call
			this.isLoading = true
			const res = await api.policyClaims({ where: { id: this.id } })
			this.isLoading = false

			// Error
			if (res.errors) this.$store.dispatch("showError", res.errors)

			// Success
			this.listing.list = res?.data?.policies?.list?.[0]?.claims || []
			this.listing.numberOfItems = this.listing.list.length
		}
	},

	mounted() {
		this.readAll()
	}
}
</script>
